import { z } from 'zod';

// Do not use z.coerce.boolean because JS type coercion from string to boolean is not logical
export const booleanSchema: z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean> = z
	.enum(['true', 'false'])
	.transform((x) => x === 'true');
export const commaSeparatedArraySchema: z.ZodEffects<z.ZodString, string[]> = z
	.string()
	.transform((x) => x.split(','));

// TODO QS-5444
// This should just be build from ProductKeys
export const productKeySchema: z.ZodNativeEnum<{
	readonly Airtable: 'airtable';
	readonly Asana: 'asana';
	readonly Atlas: 'townsquare';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Dropbox: 'dropbox';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly Gmail: 'gmail';
	readonly GoogleCalendar: 'google-calendar';
	readonly GoogleDrive: 'drive';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Loom: 'loom';
	readonly Miro: 'miro';
	readonly Notion: 'notion';
	readonly OneDrive: 'onedrive';
	readonly Opsgenie: 'opsgenie';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly Sharepoint: 'sharepoint';
	readonly Slack: 'slack';
	readonly Teams: 'teams';
	readonly Trello: 'trello';
	readonly Zendesk: 'zendesk';
}> = z.nativeEnum({
	Asana: 'asana',
	Atlas: 'townsquare',
	Bitbucket: 'bitbucket',
	Compass: 'compass',
	Confluence: 'confluence',
	GoogleDrive: 'drive',
	Jira: 'jira',
	JiraProductDiscovery: 'jira-product-discovery',
	JiraServiceManagement: 'jira-servicedesk',
	Opsgenie: 'opsgenie',
	Sharepoint: 'sharepoint',
	Slack: 'slack',
	Trello: 'trello',
	AtlassianWebCrawler: 'atlassian-web-crawler',
	Teams: 'teams',
	Figma: 'figma',
	Github: 'github',
	Loom: 'loom',
	OneDrive: 'onedrive',
	GoogleCalendar: 'google-calendar',
	Dropbox: 'dropbox',
	Gmail: 'gmail',
	OutlookCalendar: 'outlook-calendar',
	Airtable: 'airtable',
	Zendesk: 'zendesk',
	Notion: 'notion',
	Miro: 'miro',
} as const);
export type ProductKeys = z.infer<typeof productKeySchema>;
export const ProductKey: {
	readonly Airtable: 'airtable';
	readonly Asana: 'asana';
	readonly Atlas: 'townsquare';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Dropbox: 'dropbox';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly Gmail: 'gmail';
	readonly GoogleCalendar: 'google-calendar';
	readonly GoogleDrive: 'drive';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Loom: 'loom';
	readonly Miro: 'miro';
	readonly Notion: 'notion';
	readonly OneDrive: 'onedrive';
	readonly Opsgenie: 'opsgenie';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly Sharepoint: 'sharepoint';
	readonly Slack: 'slack';
	readonly Teams: 'teams';
	readonly Trello: 'trello';
	readonly Zendesk: 'zendesk';
} = productKeySchema.enum;

// TODO QS-5444
// This should be built from constants/filters
export const allContentTypeSchema: z.ZodNativeEnum<{
	All: string;
}> = z.nativeEnum({
	All: 'all',
});
export type AllContentTypes = z.infer<typeof allContentTypeSchema>;
export const AllContentType: {
	All: string;
} = allContentTypeSchema.enum;

export const atlasContentTypeSchema: z.ZodNativeEnum<{
	readonly Goal: 'goal';
	readonly Learning: 'learning';
	readonly Project: 'project';
	readonly Tag: 'tag';
}> = z.nativeEnum({
	Goal: 'goal',
	Learning: 'learning',
	Project: 'project',
	Tag: 'tag',
} as const);
export type AtlasContentTypes = z.infer<typeof atlasContentTypeSchema>;
export const AtlasContentType: {
	readonly Goal: 'goal';
	readonly Learning: 'learning';
	readonly Project: 'project';
	readonly Tag: 'tag';
} = atlasContentTypeSchema.enum;

export const asanaContentTypeSchema: z.ZodNativeEnum<{
	readonly WorkItem: 'work-item';
}> = z.nativeEnum({
	WorkItem: 'work-item',
} as const);

// oxlint-disable-next-line eslint/no-redeclare
export type AsanaContentType = z.infer<typeof asanaContentTypeSchema>;
export const AsanaContentType: {
	readonly WorkItem: 'work-item';
} = asanaContentTypeSchema.enum;

export const confluenceContentTypeSchema: z.ZodNativeEnum<{
	readonly Answer: 'answer';
	readonly Attachment: 'attachment';
	readonly Blogpost: 'blogpost';
	readonly Calendar: 'calendar';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Document: 'document';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Page: 'page';
	readonly Question: 'question';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly Whiteboard: 'whiteboard';
}> = z.nativeEnum({
	Answer: 'answer',
	Attachment: 'attachment',
	Blogpost: 'blogpost',
	Calendar: 'calendar',
	Comment: 'comment',
	Database: 'database',
	Document: 'document',
	Embed: 'embed',
	Folder: 'folder',
	Page: 'page',
	Question: 'question',
	Space: 'space',
	SpaceCalendar: 'space-calendar',
	Whiteboard: 'whiteboard',
} as const);
export type ConfluenceContentTypes = z.infer<typeof confluenceContentTypeSchema>;
export const ConfluenceContentType: {
	readonly Answer: 'answer';
	readonly Attachment: 'attachment';
	readonly Blogpost: 'blogpost';
	readonly Calendar: 'calendar';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Document: 'document';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Page: 'page';
	readonly Question: 'question';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly Whiteboard: 'whiteboard';
} = confluenceContentTypeSchema.enum;

export const googleContentTypeSchema: z.ZodNativeEnum<{
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
}> = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
} as const);
export type GoogleContentTypes = z.infer<typeof googleContentTypeSchema>;
export const GoogleContentType: {
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
} = googleContentTypeSchema.enum;

export const microsoftContentTypeSchema: z.ZodNativeEnum<{
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
}> = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
} as const);
export type MicrosoftContentTypes = z.infer<typeof microsoftContentTypeSchema>;
export const MicrosoftContentType: {
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
} = microsoftContentTypeSchema.enum;

export const dropboxContentTypeSchema: z.ZodNativeEnum<{
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
}> = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
} as const);
export type DropboxContentTypes = z.infer<typeof dropboxContentTypeSchema>;
export const DropboxContentType: {
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
} = dropboxContentTypeSchema.enum;

export const jiraContentTypeSchema: z.ZodNativeEnum<{
	readonly Board: 'board';
	readonly Dashboard: 'dashboard';
	readonly Filter: 'filter';
	readonly Issue: 'issue';
	readonly Project: 'project';
}> = z.nativeEnum({
	Project: 'project',
	Issue: 'issue',
	Filter: 'filter',
	Dashboard: 'dashboard',
	Board: 'board',
} as const);
export type JiraContentTypes = z.infer<typeof jiraContentTypeSchema>;
export const JiraContentType: {
	readonly Board: 'board';
	readonly Dashboard: 'dashboard';
	readonly Filter: 'filter';
	readonly Issue: 'issue';
	readonly Project: 'project';
} = jiraContentTypeSchema.enum;

export const compassContentTypeSchema: z.ZodNativeEnum<{
	readonly Component: 'component';
}> = z.nativeEnum({
	Component: 'component',
} as const);
export type CompassContentTypes = z.infer<typeof compassContentTypeSchema>;
export const CompassContentType: {
	readonly Component: 'component';
} = compassContentTypeSchema.enum;

export const trelloContentTypeSchema: z.ZodNativeEnum<{
	readonly Board: 'board';
	readonly Card: 'card';
}> = z.nativeEnum({
	Board: 'board',
	Card: 'card',
} as const);
export type TrelloContentTypes = z.infer<typeof trelloContentTypeSchema>;
export const TrelloContentType: {
	readonly Board: 'board';
	readonly Card: 'card';
} = trelloContentTypeSchema.enum;

export const slackContentTypeSchema: z.ZodNativeEnum<{
	readonly Conversation: 'conversation';
	readonly Message: 'message';
}> = z.nativeEnum({
	Message: 'message',
	Conversation: 'conversation',
} as const);
export type SlackContentTypes = z.infer<typeof slackContentTypeSchema>;
export const SlackContentType: {
	readonly Conversation: 'conversation';
	readonly Message: 'message';
} = slackContentTypeSchema.enum;

export const teamsContentTypeSchema: z.ZodNativeEnum<{
	readonly Conversation: 'conversation';
	readonly Message: 'message';
}> = z.nativeEnum({
	Message: 'message',
	Conversation: 'conversation',
} as const);
export type TeamsContentTypes = z.infer<typeof teamsContentTypeSchema>;
export const TeamsContentType: {
	readonly Conversation: 'conversation';
	readonly Message: 'message';
} = teamsContentTypeSchema.enum;
export const githubContentTypeSchema: z.ZodNativeEnum<{
	readonly Branch: 'branch';
	readonly Commit: 'commit';
	readonly PullRequest: 'pull-request';
	readonly Repository: 'repository';
}> = z.nativeEnum({
	Repository: 'repository',
	PullRequest: 'pull-request',
	Commit: 'commit',
	Branch: 'branch',
} as const);

// oxlint-disable-next-line eslint/no-redeclare
export type GithubContentType = z.infer<typeof githubContentTypeSchema>;
export const GithubContentType: {
	readonly Branch: 'branch';
	readonly Commit: 'commit';
	readonly PullRequest: 'pull-request';
	readonly Repository: 'repository';
} = githubContentTypeSchema.enum;

export const webPageContentTypeSchema: z.ZodNativeEnum<{
	readonly WebPage: 'web-page';
}> = z.nativeEnum({
	WebPage: 'web-page',
} as const);
export type WebPageContentTypes = z.infer<typeof webPageContentTypeSchema>;
export const WebPageContentType: {
	readonly WebPage: 'web-page';
} = webPageContentTypeSchema.enum;

export const figmaContentTypeSchema: z.ZodNativeEnum<{
	readonly Design: 'design';
}> = z.nativeEnum({
	Design: 'design',
} as const);
export type FigmaContentTypes = z.infer<typeof figmaContentTypeSchema>;
export const FigmaContentType: {
	readonly Design: 'design';
} = figmaContentTypeSchema.enum;

export const loomContentTypeSchema: z.ZodNativeEnum<{
	readonly Video: 'video';
}> = z.nativeEnum({
	Video: 'video',
} as const);
export type LoomContentTypes = z.infer<typeof loomContentTypeSchema>;
export const LoomContentType: {
	readonly Video: 'video';
} = loomContentTypeSchema.enum;

export const zendeskContentTypeSchema: z.ZodNativeEnum<{
	readonly WorkItem: 'work-item';
}> = z.nativeEnum({
	WorkItem: 'work-item',
} as const);
// oxlint-disable-next-line eslint/no-redeclare
export type ZendeskContentType = z.infer<typeof zendeskContentTypeSchema>;
export const ZendeskContentType: {
	readonly WorkItem: 'work-item';
} = zendeskContentTypeSchema.enum;

export const contentTypeSchema: z.ZodNativeEnum<{
	readonly All: string;
	readonly Answer: 'answer';
	readonly Attachment: 'attachment';
	readonly Blogpost: 'blogpost';
	readonly Board: 'board';
	readonly Branch: 'branch';
	readonly Calendar: 'calendar';
	readonly Card: 'card';
	readonly Comment: 'comment';
	readonly Commit: 'commit';
	readonly Component: 'component';
	readonly Conversation: 'conversation';
	readonly Dashboard: 'dashboard';
	readonly Database: 'database';
	readonly Design: 'design';
	readonly Document: 'document';
	readonly Embed: 'embed';
	readonly Filter: 'filter';
	readonly Folder: 'folder';
	readonly Goal: 'goal';
	readonly Issue: 'issue';
	readonly Learning: 'learning';
	readonly Message: 'message';
	readonly Page: 'page';
	readonly Presentation: 'presentation';
	readonly Project: 'project';
	readonly PullRequest: 'pull-request';
	readonly Question: 'question';
	readonly Repository: 'repository';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly Spreadsheet: 'spreadsheet';
	readonly Tag: 'tag';
	readonly Video: 'video';
	readonly WebPage: 'web-page';
	readonly Whiteboard: 'whiteboard';
	readonly WorkItem: 'work-item';
}> = z.nativeEnum({
	...AllContentType,
	...AsanaContentType,
	...AtlasContentType,
	...ConfluenceContentType,
	...GoogleContentType,
	...MicrosoftContentType,
	...JiraContentType,
	...CompassContentType,
	...TrelloContentType,
	...SlackContentType,
	...TeamsContentType,
	...FigmaContentType,
	...GithubContentType,
	...LoomContentType,
	...WebPageContentType,
	...DropboxContentType,
	...ZendeskContentType,
} as const);

export type ContentTypes = z.infer<typeof contentTypeSchema>;
export const ContentType: {
	readonly All: string;
	readonly Answer: 'answer';
	readonly Attachment: 'attachment';
	readonly Blogpost: 'blogpost';
	readonly Board: 'board';
	readonly Branch: 'branch';
	readonly Calendar: 'calendar';
	readonly Card: 'card';
	readonly Comment: 'comment';
	readonly Commit: 'commit';
	readonly Component: 'component';
	readonly Conversation: 'conversation';
	readonly Dashboard: 'dashboard';
	readonly Database: 'database';
	readonly Design: 'design';
	readonly Document: 'document';
	readonly Embed: 'embed';
	readonly Filter: 'filter';
	readonly Folder: 'folder';
	readonly Goal: 'goal';
	readonly Issue: 'issue';
	readonly Learning: 'learning';
	readonly Message: 'message';
	readonly Page: 'page';
	readonly Presentation: 'presentation';
	readonly Project: 'project';
	readonly PullRequest: 'pull-request';
	readonly Question: 'question';
	readonly Repository: 'repository';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly Spreadsheet: 'spreadsheet';
	readonly Tag: 'tag';
	readonly Video: 'video';
	readonly WebPage: 'web-page';
	readonly Whiteboard: 'whiteboard';
	readonly WorkItem: 'work-item';
} = contentTypeSchema.enum;

export const lastModifiedValueSchema: z.ZodNativeEnum<{
	readonly Anytime: 'anytime';
	readonly Custom: 'custom';
	readonly Past30Days: 'past30Days';
	readonly Past7Days: 'past7Days';
	readonly PastYear: 'pastYear';
	readonly Today: 'today';
	readonly Yesterday: 'yesterday';
}> = z.nativeEnum({
	Anytime: 'anytime',
	Today: 'today',
	Yesterday: 'yesterday',
	Past7Days: 'past7Days',
	Past30Days: 'past30Days',
	PastYear: 'pastYear',
	Custom: 'custom',
} as const);
export const LastModified: {
	readonly Anytime: 'anytime';
	readonly Custom: 'custom';
	readonly Past30Days: 'past30Days';
	readonly Past7Days: 'past7Days';
	readonly PastYear: 'pastYear';
	readonly Today: 'today';
	readonly Yesterday: 'yesterday';
} = lastModifiedValueSchema.enum;
export type LastModifiedValue = z.infer<typeof lastModifiedValueSchema>;

export const searchQueryParamsBaseSchema: z.ZodObject<
	{
		ancestor: z.ZodOptional<z.ZodString>;
		// Confluence filters
		contributors: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		from: z.ZodOptional<z.ZodString>;
		// Jira Filters
		jAssignee: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		jProject: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		labels: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		// Last Modified
		lastModified: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodNativeEnum<{
					readonly Anytime: 'anytime';
					readonly Custom: 'custom';
					readonly Past30Days: 'past30Days';
					readonly Past7Days: 'past7Days';
					readonly PastYear: 'pastYear';
					readonly Today: 'today';
					readonly Yesterday: 'yesterday';
				}>
			>
		>;
		lastModified_gt: z.ZodOptional<z.ZodPipeline<z.ZodString, z.ZodDate>>;
		lastModified_lt: z.ZodOptional<z.ZodPipeline<z.ZodString, z.ZodDate>>;
		originalQuery: z.ZodOptional<z.ZodString>;
		owners: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		product: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodNativeEnum<{
					readonly Airtable: 'airtable';
					readonly Asana: 'asana';
					readonly Atlas: 'townsquare';
					readonly AtlassianWebCrawler: 'atlassian-web-crawler';
					readonly Bitbucket: 'bitbucket';
					readonly Compass: 'compass';
					readonly Confluence: 'confluence';
					readonly Dropbox: 'dropbox';
					readonly Figma: 'figma';
					readonly Github: 'github';
					readonly Gmail: 'gmail';
					readonly GoogleCalendar: 'google-calendar';
					readonly GoogleDrive: 'drive';
					readonly Jira: 'jira';
					readonly JiraProductDiscovery: 'jira-product-discovery';
					readonly JiraServiceManagement: 'jira-servicedesk';
					readonly Loom: 'loom';
					readonly Miro: 'miro';
					readonly Notion: 'notion';
					readonly OneDrive: 'onedrive';
					readonly Opsgenie: 'opsgenie';
					readonly OutlookCalendar: 'outlook-calendar';
					readonly Sharepoint: 'sharepoint';
					readonly Slack: 'slack';
					readonly Teams: 'teams';
					readonly Trello: 'trello';
					readonly Zendesk: 'zendesk';
				}>
			>
		>;
		referrer: z.ZodOptional<z.ZodString>;
		// sort
		sortField: z.ZodOptional<z.ZodEnum<['latestModifiedDate', 'createdDate']>>;
		sortOrder: z.ZodOptional<z.ZodEnum<['ASC', 'DESC']>>;
		spaces: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		text: z.ZodOptional<z.ZodString>;
		titleMatch: z.ZodOptional<
			z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>
		>;
		to: z.ZodOptional<z.ZodString>;
		type: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodNativeEnum<{
					readonly All: string;
					readonly Answer: 'answer';
					readonly Attachment: 'attachment';
					readonly Blogpost: 'blogpost';
					readonly Board: 'board';
					readonly Branch: 'branch';
					readonly Calendar: 'calendar';
					readonly Card: 'card';
					readonly Comment: 'comment';
					readonly Commit: 'commit';
					readonly Component: 'component';
					readonly Conversation: 'conversation';
					readonly Dashboard: 'dashboard';
					readonly Database: 'database';
					readonly Design: 'design';
					readonly Document: 'document';
					readonly Embed: 'embed';
					readonly Filter: 'filter';
					readonly Folder: 'folder';
					readonly Goal: 'goal';
					readonly Issue: 'issue';
					readonly Learning: 'learning';
					readonly Message: 'message';
					readonly Page: 'page';
					readonly Presentation: 'presentation';
					readonly Project: 'project';
					readonly PullRequest: 'pull-request';
					readonly Question: 'question';
					readonly Repository: 'repository';
					readonly Space: 'space';
					readonly SpaceCalendar: 'space-calendar';
					readonly Spreadsheet: 'spreadsheet';
					readonly Tag: 'tag';
					readonly Video: 'video';
					readonly WebPage: 'web-page';
					readonly Whiteboard: 'whiteboard';
					readonly WorkItem: 'work-item';
				}>
			>
		>;
		verified: z.ZodOptional<z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>>;
	},
	'strip',
	z.ZodTypeAny,
	{
		ancestor?: string | undefined;
		contributors?: string[] | undefined;
		from?: string | undefined;
		jAssignee?: string[] | undefined;
		jProject?: string[] | undefined;
		labels?: string[] | undefined;
		lastModified?:
			| 'custom'
			| 'anytime'
			| 'today'
			| 'yesterday'
			| 'past7Days'
			| 'past30Days'
			| 'pastYear'
			| undefined;
		lastModified_gt?: Date | undefined;
		lastModified_lt?: Date | undefined;
		originalQuery?: string | undefined;
		owners?: string[] | undefined;
		product?:
			| 'confluence'
			| 'townsquare'
			| 'jira'
			| 'asana'
			| 'bitbucket'
			| 'compass'
			| 'drive'
			| 'jira-product-discovery'
			| 'jira-servicedesk'
			| 'opsgenie'
			| 'sharepoint'
			| 'slack'
			| 'trello'
			| 'atlassian-web-crawler'
			| 'teams'
			| 'figma'
			| 'github'
			| 'loom'
			| 'onedrive'
			| 'google-calendar'
			| 'dropbox'
			| 'gmail'
			| 'outlook-calendar'
			| 'airtable'
			| 'zendesk'
			| 'notion'
			| 'miro'
			| undefined;
		referrer?: string | undefined;
		sortField?: 'latestModifiedDate' | 'createdDate' | undefined;
		sortOrder?: 'ASC' | 'DESC' | undefined;
		spaces?: string[] | undefined;
		text?: string | undefined;
		titleMatch?: boolean | undefined;
		to?: string | undefined;
		type?: string | undefined;
		verified?: boolean | undefined;
	},
	{
		ancestor?: string | undefined;
		contributors?: string | undefined;
		from?: string | undefined;
		jAssignee?: string | undefined;
		jProject?: string | undefined;
		labels?: string | undefined;
		lastModified?: string | undefined;
		lastModified_gt?: string | undefined;
		lastModified_lt?: string | undefined;
		originalQuery?: string | undefined;
		owners?: string | undefined;
		product?: string | undefined;
		referrer?: string | undefined;
		sortField?: 'latestModifiedDate' | 'createdDate' | undefined;
		sortOrder?: 'ASC' | 'DESC' | undefined;
		spaces?: string | undefined;
		text?: string | undefined;
		titleMatch?: 'true' | 'false' | undefined;
		to?: string | undefined;
		type?: string | undefined;
		verified?: 'true' | 'false' | undefined;
	}
> = z.object({
	text: z.optional(z.string().max(200)), // TODO: >>>> Ask how long should be the max query string?
	originalQuery: z.optional(z.string().max(200)), // Used to support "Did you mean" feature
	product: z.optional(z.string().pipe(productKeySchema)),
	type: z.optional(z.string().pipe(contentTypeSchema)),

	// Last Modified
	lastModified: z.optional(z.string().pipe(lastModifiedValueSchema)),
	lastModified_lt: z.optional(z.string().pipe(z.coerce.date())),
	lastModified_gt: z.optional(z.string().pipe(z.coerce.date())),
	from: z.optional(z.string()),
	to: z.optional(z.string()),

	titleMatch: z.optional(booleanSchema),

	// Confluence filters
	contributors: z.optional(commaSeparatedArraySchema),
	owners: z.optional(commaSeparatedArraySchema),
	ancestor: z.optional(z.string()),
	spaces: z.optional(commaSeparatedArraySchema),
	verified: z.optional(booleanSchema),
	labels: z.optional(commaSeparatedArraySchema),

	// Jira Filters
	jAssignee: z.optional(commaSeparatedArraySchema),
	jProject: z.optional(commaSeparatedArraySchema),

	referrer: z.optional(z.string()),

	// sort
	sortField: z.optional(z.enum(['latestModifiedDate', 'createdDate'])),
	sortOrder: z.optional(z.enum(['ASC', 'DESC'])),
});

export type SearchQueryStringParams = z.infer<typeof searchQueryParamsBaseSchema>;
export const SearchQueryStringParamKey: z.Values<
	[
		'product',
		'type',
		'text',
		'originalQuery',
		'lastModified',
		'lastModified_lt',
		'lastModified_gt',
		'from',
		'to',
		'titleMatch',
		'contributors',
		'owners',
		'ancestor',
		'spaces',
		'verified',
		'labels',
		'jAssignee',
		'jProject',
		'referrer',
		'sortField',
		'sortOrder',
	]
> = searchQueryParamsBaseSchema.keyof().enum;
export type SearchQueryStringParamKeys = keyof SearchQueryStringParams;
export type SearchQueryStringParamsRaw = z.input<typeof searchQueryParamsBaseSchema>;
type ExtractKeysWithArrayValues<T> = {
	[P in keyof T]: T[P] extends unknown[] ? P : never;
}[keyof T];
export type SearchQueryStringParamKeysWithValues = NonNullable<
	ExtractKeysWithArrayValues<SearchQueryStringParams>
>;

export const searchQueryParamsValidators: ((queryParams: {
	ancestor?: string | undefined;
	contributors?: string[] | undefined;
	from?: string | undefined;
	jAssignee?: string[] | undefined;
	jProject?: string[] | undefined;
	labels?: string[] | undefined;
	lastModified?:
		| 'custom'
		| 'anytime'
		| 'today'
		| 'yesterday'
		| 'past7Days'
		| 'past30Days'
		| 'pastYear'
		| undefined;
	lastModified_gt?: Date | undefined;
	lastModified_lt?: Date | undefined;
	originalQuery?: string | undefined;
	owners?: string[] | undefined;
	product?:
		| 'confluence'
		| 'townsquare'
		| 'jira'
		| 'asana'
		| 'bitbucket'
		| 'compass'
		| 'drive'
		| 'jira-product-discovery'
		| 'jira-servicedesk'
		| 'opsgenie'
		| 'sharepoint'
		| 'slack'
		| 'trello'
		| 'atlassian-web-crawler'
		| 'teams'
		| 'figma'
		| 'github'
		| 'loom'
		| 'onedrive'
		| 'google-calendar'
		| 'dropbox'
		| 'gmail'
		| 'outlook-calendar'
		| 'airtable'
		| 'zendesk'
		| 'notion'
		| 'miro'
		| undefined;
	referrer?: string | undefined;
	sortField?: 'latestModifiedDate' | 'createdDate' | undefined;
	sortOrder?: 'ASC' | 'DESC' | undefined;
	spaces?: string[] | undefined;
	text?: string | undefined;
	titleMatch?: boolean | undefined;
	to?: string | undefined;
	type?: string | undefined;
	verified?: boolean | undefined;
}) => {
	ancestor?: string | undefined;
	contributors?: string[] | undefined;
	from?: string | undefined;
	jAssignee?: string[] | undefined;
	jProject?: string[] | undefined;
	labels?: string[] | undefined;
	originalQuery?: string | undefined;
	owners?: string[] | undefined;
	product?:
		| 'confluence'
		| 'townsquare'
		| 'jira'
		| 'asana'
		| 'bitbucket'
		| 'compass'
		| 'drive'
		| 'jira-product-discovery'
		| 'jira-servicedesk'
		| 'opsgenie'
		| 'sharepoint'
		| 'slack'
		| 'trello'
		| 'atlassian-web-crawler'
		| 'teams'
		| 'figma'
		| 'github'
		| 'loom'
		| 'onedrive'
		| 'google-calendar'
		| 'dropbox'
		| 'gmail'
		| 'outlook-calendar'
		| 'airtable'
		| 'zendesk'
		| 'notion'
		| 'miro'
		| undefined;
	referrer?: string | undefined;
	sortField?: 'latestModifiedDate' | 'createdDate' | undefined;
	sortOrder?: 'ASC' | 'DESC' | undefined;
	spaces?: string[] | undefined;
	text?: string | undefined;
	titleMatch?: boolean | undefined;
	to?: string | undefined;
	type?: string | undefined;
	verified?: boolean | undefined;
})[] = [
	(queryParams) => {
		if (queryParams.lastModified !== 'custom') {
			const { lastModified_gt, lastModified_lt, ...restQueryParams } = queryParams;
			return restQueryParams;
		}
		return queryParams;
	},
	(queryParams: {
		ancestor?: string | undefined;
		contributors?: string[] | undefined;
		from?: string | undefined;
		jAssignee?: string[] | undefined;
		jProject?: string[] | undefined;
		labels?: string[] | undefined;
		lastModified?:
			| 'custom'
			| 'anytime'
			| 'today'
			| 'yesterday'
			| 'past7Days'
			| 'past30Days'
			| 'pastYear'
			| undefined;
		lastModified_gt?: Date | undefined;
		lastModified_lt?: Date | undefined;
		originalQuery?: string | undefined;
		owners?: string[] | undefined;
		product?:
			| 'confluence'
			| 'townsquare'
			| 'jira'
			| 'asana'
			| 'bitbucket'
			| 'compass'
			| 'drive'
			| 'jira-product-discovery'
			| 'jira-servicedesk'
			| 'opsgenie'
			| 'sharepoint'
			| 'slack'
			| 'trello'
			| 'atlassian-web-crawler'
			| 'teams'
			| 'figma'
			| 'github'
			| 'loom'
			| 'onedrive'
			| 'google-calendar'
			| 'dropbox'
			| 'gmail'
			| 'outlook-calendar'
			| 'airtable'
			| 'zendesk'
			| 'notion'
			| 'miro'
			| undefined;
		referrer?: string | undefined;
		sortField?: 'latestModifiedDate' | 'createdDate' | undefined;
		sortOrder?: 'ASC' | 'DESC' | undefined;
		spaces?: string[] | undefined;
		text?: string | undefined;
		titleMatch?: boolean | undefined;
		to?: string | undefined;
		type?: string | undefined;
		verified?: boolean | undefined;
	}): {
		ancestor?: string | undefined;
		contributors?: string[] | undefined;
		from?: string | undefined;
		jAssignee?: string[] | undefined;
		jProject?: string[] | undefined;
		labels?: string[] | undefined;
		originalQuery?: string | undefined;
		owners?: string[] | undefined;
		product?:
			| 'confluence'
			| 'townsquare'
			| 'jira'
			| 'asana'
			| 'bitbucket'
			| 'compass'
			| 'drive'
			| 'jira-product-discovery'
			| 'jira-servicedesk'
			| 'opsgenie'
			| 'sharepoint'
			| 'slack'
			| 'trello'
			| 'atlassian-web-crawler'
			| 'teams'
			| 'figma'
			| 'github'
			| 'loom'
			| 'onedrive'
			| 'google-calendar'
			| 'dropbox'
			| 'gmail'
			| 'outlook-calendar'
			| 'airtable'
			| 'zendesk'
			| 'notion'
			| 'miro'
			| undefined;
		referrer?: string | undefined;
		sortField?: 'latestModifiedDate' | 'createdDate' | undefined;
		sortOrder?: 'ASC' | 'DESC' | undefined;
		spaces?: string[] | undefined;
		text?: string | undefined;
		titleMatch?: boolean | undefined;
		to?: string | undefined;
		type?: string | undefined;
		verified?: boolean | undefined;
	} => {
		const { lastModified_lt, lastModified_gt } = queryParams;
		if (lastModified_lt && lastModified_gt && lastModified_lt > lastModified_gt) {
			const { lastModified, lastModified_gt, lastModified_lt, ...restQueryParams } = queryParams;
			return restQueryParams;
		}
		return queryParams;
	},
] satisfies ((queryParams: SearchQueryStringParams) => SearchQueryStringParams)[];

export const filterKeysSchema: z.ZodNativeEnum<{
	readonly Airtable: 'airtable';
	readonly All: string;
	readonly ancestor: 'ancestor';
	readonly Answer: 'answer';
	readonly Anytime: 'anytime';
	readonly Asana: 'asana';
	readonly Atlas: 'townsquare';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Attachment: 'attachment';
	readonly Bitbucket: 'bitbucket';
	readonly Blogpost: 'blogpost';
	readonly Board: 'board';
	readonly Branch: 'branch';
	readonly Calendar: 'calendar';
	readonly Card: 'card';
	readonly Comment: 'comment';
	readonly Commit: 'commit';
	readonly Compass: 'compass';
	readonly Component: 'component';
	readonly Confluence: 'confluence';
	readonly contributors: 'contributors';
	readonly Conversation: 'conversation';
	readonly Custom: 'custom';
	readonly Dashboard: 'dashboard';
	readonly Database: 'database';
	readonly Design: 'design';
	readonly Document: 'document';
	readonly Dropbox: 'dropbox';
	readonly Embed: 'embed';
	readonly Figma: 'figma';
	readonly Filter: 'filter';
	readonly Folder: 'folder';
	readonly from: 'from';
	readonly Github: 'github';
	readonly Gmail: 'gmail';
	readonly Goal: 'goal';
	readonly GoogleCalendar: 'google-calendar';
	readonly GoogleDrive: 'drive';
	readonly Issue: 'issue';
	readonly jAssignee: 'jAssignee';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly jProject: 'jProject';
	readonly labels: 'labels';
	readonly lastModified: 'lastModified';
	readonly lastModified_gt: 'lastModified_gt';
	readonly lastModified_lt: 'lastModified_lt';
	readonly Learning: 'learning';
	readonly Loom: 'loom';
	readonly Message: 'message';
	readonly Miro: 'miro';
	readonly Notion: 'notion';
	readonly OneDrive: 'onedrive';
	readonly Opsgenie: 'opsgenie';
	readonly originalQuery: 'originalQuery';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly owners: 'owners';
	readonly Page: 'page';
	readonly Past30Days: 'past30Days';
	readonly Past7Days: 'past7Days';
	readonly PastYear: 'pastYear';
	readonly Presentation: 'presentation';
	readonly product: 'product';
	readonly Project: 'project';
	readonly PullRequest: 'pull-request';
	readonly Question: 'question';
	readonly referrer: 'referrer';
	readonly Repository: 'repository';
	readonly Sharepoint: 'sharepoint';
	readonly Slack: 'slack';
	readonly sortField: 'sortField';
	readonly sortOrder: 'sortOrder';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly spaces: 'spaces';
	readonly Spreadsheet: 'spreadsheet';
	readonly Tag: 'tag';
	readonly Teams: 'teams';
	readonly text: 'text';
	readonly titleMatch: 'titleMatch';
	readonly to: 'to';
	readonly Today: 'today';
	readonly Trello: 'trello';
	readonly type: 'type';
	readonly verified: 'verified';
	readonly Video: 'video';
	readonly WebPage: 'web-page';
	readonly Whiteboard: 'whiteboard';
	readonly WorkItem: 'work-item';
	readonly Yesterday: 'yesterday';
	readonly Zendesk: 'zendesk';
}> = z.nativeEnum({
	...ProductKey,
	...ContentType,
	...LastModified,
	...SearchQueryStringParamKey,
} as const);
export const FilterKey: {
	readonly Airtable: 'airtable';
	readonly All: string;
	readonly ancestor: 'ancestor';
	readonly Answer: 'answer';
	readonly Anytime: 'anytime';
	readonly Asana: 'asana';
	readonly Atlas: 'townsquare';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Attachment: 'attachment';
	readonly Bitbucket: 'bitbucket';
	readonly Blogpost: 'blogpost';
	readonly Board: 'board';
	readonly Branch: 'branch';
	readonly Calendar: 'calendar';
	readonly Card: 'card';
	readonly Comment: 'comment';
	readonly Commit: 'commit';
	readonly Compass: 'compass';
	readonly Component: 'component';
	readonly Confluence: 'confluence';
	readonly contributors: 'contributors';
	readonly Conversation: 'conversation';
	readonly Custom: 'custom';
	readonly Dashboard: 'dashboard';
	readonly Database: 'database';
	readonly Design: 'design';
	readonly Document: 'document';
	readonly Dropbox: 'dropbox';
	readonly Embed: 'embed';
	readonly Figma: 'figma';
	readonly Filter: 'filter';
	readonly Folder: 'folder';
	readonly from: 'from';
	readonly Github: 'github';
	readonly Gmail: 'gmail';
	readonly Goal: 'goal';
	readonly GoogleCalendar: 'google-calendar';
	readonly GoogleDrive: 'drive';
	readonly Issue: 'issue';
	readonly jAssignee: 'jAssignee';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly jProject: 'jProject';
	readonly labels: 'labels';
	readonly lastModified: 'lastModified';
	readonly lastModified_gt: 'lastModified_gt';
	readonly lastModified_lt: 'lastModified_lt';
	readonly Learning: 'learning';
	readonly Loom: 'loom';
	readonly Message: 'message';
	readonly Miro: 'miro';
	readonly Notion: 'notion';
	readonly OneDrive: 'onedrive';
	readonly Opsgenie: 'opsgenie';
	readonly originalQuery: 'originalQuery';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly owners: 'owners';
	readonly Page: 'page';
	readonly Past30Days: 'past30Days';
	readonly Past7Days: 'past7Days';
	readonly PastYear: 'pastYear';
	readonly Presentation: 'presentation';
	readonly product: 'product';
	readonly Project: 'project';
	readonly PullRequest: 'pull-request';
	readonly Question: 'question';
	readonly referrer: 'referrer';
	readonly Repository: 'repository';
	readonly Sharepoint: 'sharepoint';
	readonly Slack: 'slack';
	readonly sortField: 'sortField';
	readonly sortOrder: 'sortOrder';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly spaces: 'spaces';
	readonly Spreadsheet: 'spreadsheet';
	readonly Tag: 'tag';
	readonly Teams: 'teams';
	readonly text: 'text';
	readonly titleMatch: 'titleMatch';
	readonly to: 'to';
	readonly Today: 'today';
	readonly Trello: 'trello';
	readonly type: 'type';
	readonly verified: 'verified';
	readonly Video: 'video';
	readonly WebPage: 'web-page';
	readonly Whiteboard: 'whiteboard';
	readonly WorkItem: 'work-item';
	readonly Yesterday: 'yesterday';
	readonly Zendesk: 'zendesk';
} = filterKeysSchema.enum;
export type FilterKeys = z.infer<typeof filterKeysSchema>;
export type FilterKeysRaw = boolean | readonly string[];
